import type {
  DisplayProfileBackgroundTheme,
  DisplayProfileGridDensity,
} from "../types/eegViewerDisplayProfileTypes";
import {
  ROW_HEIGHT_CHANNEL_SPACING,
  type RowHeightMode,
} from "../components/eegViewerDisplayHelpers";

export const EEG_PAPER_THEME_STORAGE_KEY = "eeg-viewer-paper-theme";

export type PortalWorkspaceDensity = "comfortable" | "cozy" | "compact";

export function parseEegPaperTheme(
  value: unknown,
): DisplayProfileBackgroundTheme | null {
  if (value === "light" || value === "dark" || value === "high_contrast") {
    return value;
  }
  return null;
}

/** Display Preferences uses hyphenated high-contrast; EEG paper uses underscore. */
export function portalThemeToEegPaperTheme(
  theme: string | null | undefined,
  highContrast = false,
): DisplayProfileBackgroundTheme {
  if (highContrast || theme === "high-contrast" || theme === "high_contrast") {
    return "high_contrast";
  }
  if (theme === "dark") return "dark";
  if (theme === "auto") {
    try {
      if (globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches) {
        return "dark";
      }
    } catch {
      // matchMedia is unavailable in some test / SSR environments.
    }
    return "light";
  }
  return "light";
}

export function parsePortalWorkspaceDensity(
  value: unknown,
): PortalWorkspaceDensity {
  if (value === "comfortable" || value === "cozy" || value === "compact") {
    return value;
  }
  return "cozy";
}

export function workspaceDensityToEegVisual(density: PortalWorkspaceDensity): {
  gridDensity: DisplayProfileGridDensity;
  rowHeightMode: RowHeightMode;
  channelSpacing: number;
} {
  if (density === "comfortable") {
    return {
      gridDensity: "low",
      rowHeightMode: "tall",
      channelSpacing: ROW_HEIGHT_CHANNEL_SPACING.tall,
    };
  }
  if (density === "compact") {
    return {
      gridDensity: "high",
      rowHeightMode: "compact",
      channelSpacing: ROW_HEIGHT_CHANNEL_SPACING.compact,
    };
  }
  return {
    gridDensity: "medium",
    rowHeightMode: "standard",
    channelSpacing: ROW_HEIGHT_CHANNEL_SPACING.standard,
  };
}

export function eegVisualToWorkspaceDensity(
  gridDensity: DisplayProfileGridDensity,
  channelSpacing?: number,
): PortalWorkspaceDensity {
  if (typeof channelSpacing === "number" && Number.isFinite(channelSpacing)) {
    const compactDistance = Math.abs(
      channelSpacing - ROW_HEIGHT_CHANNEL_SPACING.compact,
    );
    const tallDistance = Math.abs(
      channelSpacing - ROW_HEIGHT_CHANNEL_SPACING.tall,
    );
    const standardDistance = Math.abs(
      channelSpacing - ROW_HEIGHT_CHANNEL_SPACING.standard,
    );
    if (
      compactDistance <= standardDistance &&
      compactDistance <= tallDistance
    ) {
      return "compact";
    }
    if (tallDistance <= standardDistance && tallDistance < compactDistance) {
      return "comfortable";
    }
  }
  if (gridDensity === "high") return "compact";
  if (gridDensity === "low") return "comfortable";
  return "cozy";
}

export function rowHeightModeToGridDensity(
  mode: RowHeightMode,
): DisplayProfileGridDensity {
  if (mode === "compact") return "high";
  if (mode === "tall") return "low";
  return "medium";
}

export function readStoredEegPaperTheme(): DisplayProfileBackgroundTheme | null {
  try {
    return parseEegPaperTheme(
      globalThis.localStorage?.getItem(EEG_PAPER_THEME_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

export function writeStoredEegPaperTheme(
  theme: DisplayProfileBackgroundTheme,
): void {
  try {
    globalThis.localStorage?.setItem(EEG_PAPER_THEME_STORAGE_KEY, theme);
  } catch {
    // Private mode / disabled storage should not block theme changes.
  }
}

/**
 * Workspace chrome + paper theme for the EEG review window.
 * Accessibility high-contrast always wins over the display-profile Theme dropdown.
 */
export function resolveEegWorkspaceTheme(options: {
  profileTheme: DisplayProfileBackgroundTheme;
  accessibilityHighContrast: boolean;
}): DisplayProfileBackgroundTheme {
  if (options.accessibilityHighContrast) {
    return "high_contrast";
  }
  return options.profileTheme;
}

export type EegOverviewPalette = {
  bg: string;
  tickBand: string;
  tick: string;
  tickMajor: string;
  label: string;
  grid: string;
  reviewedFill: string;
  viewportFill: string;
};

const DARK_OVERVIEW_PALETTE: EegOverviewPalette = {
  bg: "#0b1220",
  tickBand: "rgba(17, 24, 39, 0.95)",
  tick: "#334155",
  tickMajor: "#475569",
  label: "#94a3b8",
  grid: "#1e293b",
  reviewedFill: "rgba(34, 197, 94, 0.22)",
  viewportFill: "rgba(148, 163, 184, 0.14)",
};

const LIGHT_OVERVIEW_PALETTE: EegOverviewPalette = {
  bg: "#e8edf2",
  tickBand: "#f4f6f8",
  tick: "#cbd5e1",
  tickMajor: "#64748b",
  label: "#334155",
  grid: "#d5dde5",
  reviewedFill: "rgba(22, 163, 74, 0.28)",
  viewportFill: "rgba(15, 23, 42, 0.1)",
};

/** Canvas fill/stroke colors for the study overview / annotation strip. */
export function resolveEegOverviewPalette(
  theme: DisplayProfileBackgroundTheme | string | null | undefined,
): EegOverviewPalette {
  return theme === "light" ? LIGHT_OVERVIEW_PALETTE : DARK_OVERVIEW_PALETTE;
}
